import { DatePipe } from '@angular/common';
import { Injectable } from '@angular/core';
import { BehaviorSubject, map, tap } from 'rxjs';
import { DynamicDocumentService } from 'src/app/shared/services/dynamic-document.service';

const COLLECTION_NAME = 'dynamic_bookmark';

export type Favorite = {
    id: string;
    name?: string;
    obj?: any;
};

@Injectable({ providedIn: 'root' })
export class FavoriteRepository {
    public favorites$ = new BehaviorSubject<Favorite[]>([]);

    favorites: Favorite[] = [];

    favoritedProductIds = new Map<string, string>(); //productId => favId

    constructor(
        private dynamicDocumentService: DynamicDocumentService,
        private datePipe: DatePipe
    ) {}
    getMyFavorites() {
        return this.dynamicDocumentService
            .find(
                {
                    collectionName: COLLECTION_NAME,
                    filters: [
                        {
                            fieldName: 'linkToNaturalPersons',
                            conditions: [
                                {
                                    type: 'EQUALS',
                                    value: localStorage.getItem('surname'),
                                },
                            ],
                        },
                    ],
                    page: 1,
                    pageSize: 100,
                    sorts: {
                        updatingDate: 'DESC',
                    },
                },
                true
            )
            .pipe(map((res) => res?.content?.map((fav: any) => this.convertFavorite(fav))))
            .subscribe((favs) => {
                this.updateFavorites(favs);
            });
    }

    delete(favId: string) {
        return this.dynamicDocumentService.deleteById(COLLECTION_NAME, favId).pipe(
            tap(() => {
                const result = this.favorites?.filter((f) => f.id !== favId);
                this.updateFavorites(result);
            })
        );
    }

    add(name: string) {
        return this.dynamicDocumentService
            .saveDocument(
                COLLECTION_NAME,
                { name, linkToNaturalPersons: [localStorage.getItem('userid')], linkToProduct: [] },
                true
            )
            .pipe(
                tap((res) => {
                    const newFav = this.convertFavorite(res);
                    const newFavs = this.favorites?.concat(newFav);
                    this.updateFavorites(newFavs);
                })
            );
    }

    edit(fav: Favorite) {
        fav.obj.name = fav.name;
        fav.obj.creationDate = this.datePipe.transform(
            fav.obj.creationDate,
            "yyyy-MM-dd'T'HH:mm:ss"
        );
        fav.obj.updatingDate = this.datePipe.transform(Date.now(), "yyyy-MM-dd'T'00:00:00");

        return this.dynamicDocumentService.saveDocument(COLLECTION_NAME, fav.obj, true).pipe(
            tap((res) => {
                const newFav = this.convertFavorite(res);
                const newFavs = this.favorites?.filter((f) => f.id !== fav.id).concat(newFav);
                this.updateFavorites(newFavs);
            })
        );
    }

    isFavorited(pId: string) {
        return this.favoritedProductIds?.has(pId);
    }

    unFavorite(pId: string) {
        const favId = this.favoritedProductIds.get(pId);
        const fav = this.favorites?.find((f) => f.id === favId);

        if (!fav) return;

        fav.obj.creationDate = this.datePipe.transform(
            fav.obj.creationDate,
            "yyyy-MM-dd'T'HH:mm:ss"
        );
        fav.obj.updatingDate = this.datePipe.transform(Date.now(), "yyyy-MM-dd'T'00:00:00");

        fav.obj.linkToProduct = fav.obj.linkToProduct?.filter((p: any) => p.documentId !== pId);

        this.dynamicDocumentService
            .saveDocument(COLLECTION_NAME, fav.obj, true)
            .subscribe((res) => {
                const newFav = this.convertFavorite(res);
                const newFavs = this.favorites?.filter((f) => f.id !== favId).concat(newFav);
                this.updateFavorites(newFavs);
            });
    }

    favorite(favId: string, pId: string) {
        const fav = this.favorites?.find((f) => f.id === favId);

        if (!fav) return;

        fav.obj.creationDate = this.datePipe.transform(
            fav.obj.creationDate,
            "yyyy-MM-dd'T'HH:mm:ss"
        );
        fav.obj.updatingDate = this.datePipe.transform(Date.now(), "yyyy-MM-dd'T'00:00:00");

        fav.obj.linkToProduct = fav.obj.linkToProduct?.concat(pId);

        this.dynamicDocumentService
            .saveDocument(COLLECTION_NAME, fav.obj, true)
            .subscribe((res) => {
                const newFav = this.convertFavorite(res);
                const newFavs = this.favorites?.filter((f) => f.id !== favId).concat(newFav);
                this.updateFavorites(newFavs);
            });
    }

    // --------
    updateFavorites(favs: Favorite[]) {
        this.favorites = favs;
        this.favorites$.next(this.favorites ? this.favorites : []);

        this.favoritedProductIds.clear();
        this.favorites?.forEach((fav) => {
            fav.obj?.linkToProduct?.forEach((p: any) => {
                this.favoritedProductIds.set(p.documentId, fav.id);
            });
        });
    }
    convertFavorite(data: any): Favorite {
        return {
            id: data?.id,
            name: data?.name,
            obj: data,
        };
    }

    getProductsByFavId(favId: string) {
        const productIds: string[] = [];
        this.favoritedProductIds.forEach((favId2, productId) => {
            if (favId2 === favId) {
                productIds.push(productId);
            }
        });

        return productIds;
    }

    getMyFavoriteList() {
        return this.dynamicDocumentService
            .find(
                {
                    collectionName: COLLECTION_NAME,
                    filters: [
                        {
                            fieldName: 'linkToNaturalPersons',
                            conditions: [
                                {
                                    type: 'EQUALS',
                                    value: localStorage.getItem('surname'),
                                },
                            ],
                        },
                    ],
                    page: 1,
                    pageSize: 100,
                    sorts: {
                        updatingDate: 'DESC',
                    },
                },
                true
            )
            .pipe(map((res) => res?.content?.map((fav: any) => this.convertFavorite(fav))));
    }
}
